import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  DatePicker,
  Descriptions,
  Divider,
  Drawer,
  Empty,
  Input,
  List,
  Progress,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useFetchData } from '../../../hooks';
import {
  ProderProgressDetail,
  ProderProgressResponse,
  ProderProgressRow,
  ProderProgressState,
} from '../../../types';
import PageHeaderAction from '../../../components/PageHeaderAction';
import ProderPlanSection from './ProderPlanSection';
import {
  convertDate,
  dateFormat,
  dateNameTimeFormat,
} from '../../../lib/utils';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const STATE_COLOUR: Record<ProderProgressState, string> = {
  NOT_STARTED: 'default',
  IN_PROGRESS: 'processing',
  FINISHED: 'cyan',
  PART_SHIPPED: 'gold',
  SHIPPED: 'green',
};

const stateOptions = (
  ['NOT_STARTED', 'IN_PROGRESS', 'FINISHED', 'PART_SHIPPED', 'SHIPPED'] as const
).map((s) => ({ label: s, value: s }));

// Flow order and display style for the known processes; anything else falls
// back to the first 4 letters and a grey bar.
const PROCESS_META: Record<string, { short: string; color: string }> = {
  Greenware: { short: 'Grn', color: '#722ed1' },
  'FG Breaks': { short: 'FGB', color: '#eb2f96' },
  Glazing: { short: 'Glz', color: '#2f54eb' },
  Finish: { short: 'Fin', color: '#13c2c2' },
  Attachment: { short: 'Att', color: '#fa8c16' },
  Packing: { short: 'Pck', color: '#52c41a' },
  'Others Packing': { short: 'OPk', color: '#a0d911' },
};
const processMeta = (process: string) =>
  PROCESS_META[process] ?? {
    short: process.slice(0, 4),
    color: '#8c8c8c',
  };

const pct = (done: number, order: number | null) =>
  order !== null && order > 0 ? Math.round((100 * done) / order) : 0;

/** embedded: shown as a tab of the Planning page, which carries the header. */
const ProderProgressPage: React.FC<{ embedded?: boolean }> = ({
  embedded = false,
}) => {
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs(),
    dayjs().add(90, 'day'),
  ]);
  const [buyer, setBuyer] = useState('');
  const [state, setState] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [selectedProder, setSelectedProder] = useState<string | undefined>();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const params = useMemo(() => {
    const p: Record<string, string> = {
      from: range[0].format(dateFormat),
      to: range[1].format(dateFormat),
    };
    if (buyer.trim()) p.buyer = buyer.trim();
    if (state) p.state = state;
    if (search.trim()) p.q = search.trim();
    return p;
  }, [range, buyer, state, search]);

  const { data, isLoading, isFetching, refetch } =
    useFetchData<ProderProgressResponse>('/planning/proder-progress', params);

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const inProgress = rows.filter((r) => r.state === 'IN_PROGRESS').length;
  const partShipped = rows.filter((r) => r.state === 'PART_SHIPPED').length;
  const shipped = rows.filter((r) => r.state === 'SHIPPED').length;
  const late = rows.filter((r) => (r.daysLate ?? 0) > 0).length;

  // dynamically import xlsx so the (heavy) library stays out of the main bundle
  const exportToExcel = async () => {
    const XLSX = await import('xlsx');
    const processNames: string[] = [];
    rows.forEach((r) =>
      r.processes.forEach((p) => {
        if (
          p.orderQty !== null &&
          p.orderQty > 0 &&
          !processNames.includes(p.process)
        ) {
          processNames.push(p.process);
        }
      }),
    );
    const out = rows.map((r) => {
      const row: Record<string, string | number | null> = {
        Proder: r.proderNo,
        Buyer: r.buyerCode,
        'Requested Ship': r.requestedShipDate,
        'First Ship': r.firstShipDate,
        'Last Ship': r.lastShipDate,
        State: r.state,
        'Ordered (BOM)': r.orderQty,
        'Shipped Qty': r.shippedQty,
        'Ticket Total': r.ticketOrderQty,
        Mismatch: r.orderMismatch ? 'YES' : '',
        'Days Late': r.daysLate,
        'Idle Days': r.idleDays,
      };
      processNames.forEach((name) => {
        const p = r.processes.find((x) => x.process === name);
        row[name] =
          p && p.orderQty !== null && p.orderQty > 0
            ? `${p.qtyDone}/${p.orderQty}`
            : '';
      });
      return row;
    });
    const ws = XLSX.utils.json_to_sheet(out);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Proder Progress');
    XLSX.writeFile(wb, `proder-progress-${dayjs().format('YYYYMMDD')}.xlsx`);
  };

  const columns = [
    {
      title: 'Proder',
      dataIndex: 'proderNo',
      render: (v: string, r: ProderProgressRow) => (
        <Space direction={'vertical'} size={0}>
          <span className={'text-blue-400 font-semibold'}>{v}</span>
          <Space size={2} wrap>
            {r.reorder === true && (
              <Tooltip title={r.reorderEvidence}>
                <Tag className={'text-xs'}>Re-order</Tag>
              </Tooltip>
            )}
            {r.reorder === false && (
              <Tag color={'purple'} className={'text-xs'}>
                New item
              </Tag>
            )}
            {r.reorder !== true && (
              <Tag
                color={
                  r.readinessRecorded === 0
                    ? 'default'
                    : r.readinessDone === 3
                      ? 'green'
                      : 'orange'
                }
                className={'text-xs'}
              >
                {r.readinessRecorded === 0
                  ? 'readiness not recorded'
                  : `readiness ${r.readinessDone}/3`}
              </Tag>
            )}
            {r.finalStatus === 'DRAFT' && (
              <Tag color={r.finalLate ? 'red' : 'orange'} className={'text-xs'}>
                {r.finalLate ? 'final Proder late' : 'draft Proder'}
              </Tag>
            )}
            {r.shipDrift && (
              <Tag color={'red'} className={'text-xs'}>
                plan past confirmed ship
              </Tag>
            )}
            {!r.shipDrift && r.shipVerdict === 'NOT_WORKABLE' && !r.confirmedShipDate && (
              <Tag color={'red'} className={'text-xs'}>
                not workable
              </Tag>
            )}
            {r.shipAnswerPending && (
              <Tag color={'gold'} className={'text-xs'}>
                waiting for Marketing
              </Tag>
            )}
            {r.confirmedShipDate && !r.shipAnswerPending && (
              <Tag color={'blue'} className={'text-xs'}>
                ship {convertDate(r.confirmedShipDate, dateFormat)}
              </Tag>
            )}
            {r.planned && (
              <Tag color={'cyan'} className={'text-xs'}>
                planned
                {r.planFinish ? ` → ${convertDate(r.planFinish, dateFormat)}` : ''}
              </Tag>
            )}
          </Space>
        </Space>
      ),
    },
    {
      title: 'Buyer',
      dataIndex: 'buyerCode',
      render: (v: string | null) => v ?? '—',
    },
    {
      title: 'Requested Ship',
      dataIndex: 'requestedShipDate',
      render: (v: string | null) => (v ? convertDate(v, dateFormat) : '—'),
    },
    {
      title: 'Actual Ship',
      key: 'actualShip',
      render: (_: unknown, record: ProderProgressRow) =>
        record.firstShipDate ? (
          <span className={'whitespace-nowrap'}>
            {convertDate(record.firstShipDate, dateFormat)}
            {record.lastShipDate && record.lastShipDate !== record.firstShipDate
              ? ` – ${convertDate(record.lastShipDate, dateFormat)}`
              : ''}
          </span>
        ) : (
          '—'
        ),
    },
    {
      title: 'State',
      dataIndex: 'state',
      render: (v: ProderProgressState) => (
        <Tag color={STATE_COLOUR[v]}>{v}</Tag>
      ),
    },
    {
      title: 'Progress',
      key: 'progress',
      width: 220,
      render: (_: unknown, record: ProderProgressRow) => (
        <div>
          {record.processes.map((p) => {
            const meta = processMeta(p.process);
            // No active ticket for this process: the order is unknown, so
            // say so instead of implying a percentage.
            if (p.orderQty === null) {
              if (p.qtyDone === 0) return null;
              return (
                <div key={p.process} className={'flex items-center gap-1'}>
                  <span
                    className={'text-xs'}
                    style={{ width: 28, color: meta.color }}
                  >
                    {meta.short}
                  </span>
                  <Text type={'secondary'} className={'text-xs'}>
                    {p.qtyDone} done · order not set up
                  </Text>
                </div>
              );
            }
            return (
              <div
                key={p.process}
                className={'flex items-center gap-1'}
                title={`operation-pieces · ops ${p.opsStarted}/${p.opsTotal}`}
              >
                <span
                  className={'text-xs'}
                  style={{ width: 28, color: meta.color }}
                >
                  {meta.short}
                </span>
                <Progress
                  percent={pct(p.qtyDone, p.orderQty)}
                  size={'small'}
                  strokeColor={meta.color}
                  showInfo={false}
                  style={{ flex: 1, marginBottom: 0 }}
                />
              </div>
            );
          })}
        </div>
      ),
    },
    {
      title: 'Days Late',
      dataIndex: 'daysLate',
      render: (v: number | null) =>
        v === null ? (
          '—'
        ) : (
          <Text type={v > 0 ? 'danger' : undefined}>{v > 0 ? `+${v}` : v}</Text>
        ),
    },
    {
      title: 'Idle Days',
      dataIndex: 'idleDays',
      render: (v: number | null) =>
        v === null ? (
          '—'
        ) : (
          <Text style={v >= 3 ? { color: '#faad14' } : undefined}>{v}</Text>
        ),
    },
  ];

  return (
    <div className={'page-wrapper'}>
      {!embedded && (
        <PageHeaderAction moduleName={'Proder Progress'} action={null} />
      )}

      <Alert
        className={'my-2'}
        type={data?.asOf ? 'info' : 'warning'}
        showIcon
        message={
          data?.asOf
            ? `WIP data as of ${convertDate(data.asOf, dateNameTimeFormat)}`
            : 'The WIP snapshot has never been synced.'
        }
      />

      <div className={'flex items-center justify-between mb-2 flex-wrap gap-2'}>
        <Space wrap>
          <RangePicker
            allowClear={false}
            value={range}
            onChange={(v) => v && v[0] && v[1] && setRange([v[0], v[1]])}
          />
          <Input
            allowClear
            placeholder={'Buyer code'}
            style={{ width: 140 }}
            value={buyer}
            onChange={(e) => setBuyer(e.target.value)}
          />
          <Select
            placeholder={'State'}
            allowClear
            options={stateOptions}
            value={state}
            onChange={(v) => setState(v)}
            style={{ minWidth: 160 }}
          />
          <Input.Search
            allowClear
            placeholder={'Proder no.'}
            style={{ width: 180 }}
            onSearch={setSearch}
          />
        </Space>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            loading={isFetching}
            onClick={() => refetch()}
          />
          <Button icon={<DownloadOutlined />} onClick={exportToExcel}>
            Export
          </Button>
        </Space>
      </div>

      <Space split={<Divider type={'vertical'} />} className={'mb-2'} wrap>
        <Typography.Text type={'secondary'}>
          {rows.length} proder{rows.length === 1 ? '' : 's'}
        </Typography.Text>
        <Typography.Text>{inProgress} in progress</Typography.Text>
        {partShipped > 0 && (
          <Typography.Text style={{ color: '#d4b106' }}>
            {partShipped} part-shipped
          </Typography.Text>
        )}
        <Typography.Text style={{ color: '#52c41a' }}>
          {shipped} shipped
        </Typography.Text>
        {late > 0 && (
          <Typography.Text style={{ color: '#ff4d4f' }}>
            {late} late
          </Typography.Text>
        )}
      </Space>

      <Table<ProderProgressRow>
        rowKey={(record) => record.proderNo}
        dataSource={rows}
        loading={isLoading}
        columns={columns}
        size={'small'}
        pagination={{
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        onRow={(record) => ({
          onClick: () => {
            setSelectedProder(record.proderNo);
            setDrawerOpen(true);
          },
          className: 'cursor-pointer',
        })}
      />

      <Drawer
        title={
          selectedProder ? `Proder · ${selectedProder}` : 'Proder Progress'
        }
        // wide enough for the per-process plan table
        width={1040}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        {selectedProder && (
          <ProderProgressDetailView
            key={selectedProder}
            proderNo={selectedProder}
          />
        )}
      </Drawer>
    </div>
  );
};

const ProderProgressDetailView: React.FC<{ proderNo: string }> = ({
  proderNo,
}) => {
  const { data, isLoading, error } = useFetchData<ProderProgressDetail>(
    `/planning/proder-progress/${encodeURIComponent(proderNo)}`,
  );

  // Timeline spans the earliest process start to the latest output/ship date.
  const timeline = useMemo(() => {
    if (!data) return null;
    const starts = data.processes
      .map((p) => p.firstDone)
      .filter((d): d is string => !!d);
    const ends = [
      ...data.processes.map((p) => p.lastDone),
      data.firstShipDate,
      data.lastShipDate,
      data.requestedShipDate,
    ].filter((d): d is string => !!d);
    if (starts.length === 0 || ends.length === 0) return null;
    const min = starts
      .map((d) => dayjs(d))
      .reduce((a, b) => (b.isBefore(a) ? b : a));
    let max = ends
      .map((d) => dayjs(d))
      .reduce((a, b) => (b.isAfter(a) ? b : a));
    if (!max.isAfter(min)) max = min.add(1, 'day');
    const span = max.diff(min, 'day');
    const pos = (d: string | null) =>
      d === null
        ? null
        : Math.min(100, Math.max(0, (dayjs(d).diff(min, 'day') / span) * 100));
    return { min, max, pos };
  }, [data]);

  if (isLoading) {
    return (
      <div className={'flex justify-center py-10'}>
        <Spin />
      </div>
    );
  }
  if (error || !data) {
    return (
      <Empty
        description={'No WIP snapshot data for this proder.'}
        className={'py-10'}
      />
    );
  }

  const shipMarkers = Array.from(
    new Set(
      data.invoices.map((i) => i.shipDate).filter((d): d is string => !!d),
    ),
  );

  return (
    <div className={'space-y-4'}>
      <Descriptions size={'small'} column={2}>
        <Descriptions.Item label={'Proder'}>{data.proderNo}</Descriptions.Item>
        <Descriptions.Item label={'Buyer'}>
          {data.buyerCode ?? '—'}
        </Descriptions.Item>
        <Descriptions.Item label={'Requested Ship'}>
          {data.requestedShipDate
            ? convertDate(data.requestedShipDate, dateFormat)
            : '—'}
        </Descriptions.Item>
        <Descriptions.Item label={'State'}>
          <Tag color={STATE_COLOUR[data.state]}>{data.state}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label={'Ordered (BOM)'}>
          {data.orderQty ?? 'not set up'}
        </Descriptions.Item>
        <Descriptions.Item label={'Shipped'}>
          {data.shippedQty}
          {data.orderMismatch && (
            <Tag color={'red'} className={'ml-2'}>
              ticket total {data.ticketOrderQty} disagrees
            </Tag>
          )}
        </Descriptions.Item>
      </Descriptions>

      <Space wrap>
        {data.reorder === true && (
          <Tag>Re-order · {data.reorderEvidence}</Tag>
        )}
        {data.reorder === false && (
          <Tag color={'purple'}>New item — readiness gates apply</Tag>
        )}
        {data.reorder === null && (
          <Tag>New / re-order unknown: no SKU on its sets</Tag>
        )}
      </Space>

      <ProderPlanSection proderNo={data.proderNo} />

      {timeline && (
        <div
          style={{
            position: 'relative',
            height: 14,
            borderBottom: '1px solid #434343',
          }}
        >
          {data.requestedShipDate && (
            <div
              title={`Requested ship ${data.requestedShipDate}`}
              style={{
                position: 'absolute',
                left: `${timeline.pos(data.requestedShipDate)}%`,
                top: 0,
                bottom: 0,
                width: 2,
                background: '#ff4d4f',
              }}
            />
          )}
          {shipMarkers.map((d) => (
            <div
              key={d}
              title={`Shipped ${d}`}
              style={{
                position: 'absolute',
                left: `${timeline.pos(d)}%`,
                top: 2,
                bottom: 0,
                width: 2,
                background: '#52c41a',
              }}
            />
          ))}
          <div
            className={'flex justify-between text-xs'}
            style={{ position: 'absolute', top: 16, left: 0, right: 0 }}
          >
            <Text type={'secondary'}>{timeline.min.format(dateFormat)}</Text>
            <Text type={'secondary'}>{timeline.max.format(dateFormat)}</Text>
          </div>
        </div>
      )}

      {data.processes.map((p) => {
        const meta = processMeta(p.process);
        const left = timeline?.pos(p.firstDone) ?? null;
        const right = timeline?.pos(p.lastDone) ?? null;
        return (
          <div key={p.process}>
            <div className={'flex items-center justify-between'}>
              <Text strong>{p.process}</Text>
              <Text type={'secondary'} className={'text-xs'}>
                {p.firstDone && p.lastDone
                  ? `${convertDate(p.firstDone, dateFormat)} – ${convertDate(
                      p.lastDone,
                      dateFormat,
                    )}`
                  : 'No output yet'}
              </Text>
            </div>
            <div className={'flex items-center gap-2'}>
              <Progress
                percent={pct(p.qtyDone, p.orderQty)}
                size={'small'}
                strokeColor={meta.color}
                style={{ flex: 1 }}
              />
              <Text className={'text-xs whitespace-nowrap'}>
                {p.orderQty === null
                  ? `${p.qtyDone} done · order not set up`
                  : `${p.qtyDone}/${p.orderQty} · bal ${p.balance} · ops ${p.opsStarted}/${p.opsTotal}`}
              </Text>
            </div>
            <div className={'flex items-center gap-2 flex-wrap'}>
              {p.workMinutes !== null ? (
                <Text type={'secondary'} className={'text-xs'}>
                  Work content: {(p.workMinutes / 60).toFixed(1)} h
                  {p.workerDays !== null
                    ? ` · ${p.workerDays.toFixed(1)} worker-days`
                    : ' · worker-days not set up'}
                  {p.remainingMinutes !== null &&
                    ` · ~${(p.remainingMinutes / 60).toFixed(
                      1,
                    )} h remaining (estimated)`}
                </Text>
              ) : (
                <Text type={'secondary'} className={'text-xs'}>
                  Work content not set up
                </Text>
              )}
              {p.areasWithoutFactor && p.areasWithoutFactor.length > 0 && (
                <Tag color={'warning'} className={'text-xs'}>
                  no factor: {p.areasWithoutFactor.join(', ')}
                </Tag>
              )}
            </div>
            {p.areas &&
              p.areas.map((a, i) => (
                <div key={`${a.area}-${i}`}>
                  <Text type={'secondary'} className={'text-xs'}>
                    {a.area}: {a.orderPcs} pcs
                    {a.factor !== null
                      ? ` × ₱${a.pieceRate ?? '—'} × ${a.factor.toFixed(3)} min/₱ = ${
                          a.workMinutes !== null
                            ? Math.round(a.workMinutes)
                            : '—'
                        } min`
                      : ' · factor not set up'}
                    {a.factor !== null && a.factorFrom && a.factorTo
                      ? ` (rate from output ${a.factorFrom} to ${a.factorTo}${
                          a.factorSample ? `, n=${a.factorSample}` : ''
                        })`
                      : ''}
                    {a.prevFactor !== null
                      ? ` · was ${a.prevFactor.toFixed(3)} before`
                      : ''}
                  </Text>
                </div>
              ))}
            {timeline && left !== null && right !== null && (
              <div
                style={{
                  position: 'relative',
                  height: 8,
                  background: '#1f1f1f',
                  borderRadius: 4,
                  marginTop: 2,
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    left: `${left}%`,
                    width: `${Math.max(right - left, 1)}%`,
                    top: 0,
                    bottom: 0,
                    background: meta.color,
                    borderRadius: 4,
                  }}
                />
              </div>
            )}
          </div>
        );
      })}

      <Divider orientation={'left'} plain>
        Invoices
      </Divider>
      <List
        size={'small'}
        dataSource={data.invoices}
        locale={{ emptyText: 'No invoices' }}
        renderItem={(inv) => (
          <List.Item>
            <Text>{inv.invoiceNo ?? inv.invoiceId}</Text>
            <Text type={'secondary'} className={'text-xs'}>
              ship {inv.shipDate ? convertDate(inv.shipDate, dateFormat) : '—'}
              {' · invoice '}
              {inv.invoiceDate ? convertDate(inv.invoiceDate, dateFormat) : '—'}
              {' · '}
              {inv.shippedQty} pcs
            </Text>
          </List.Item>
        )}
      />

      <Divider orientation={'left'} plain>
        Tickets
      </Divider>
      <List
        size={'small'}
        dataSource={data.tickets}
        locale={{ emptyText: 'No tickets' }}
        renderItem={(t) => (
          <List.Item>
            <Text>{t.ticketNo}</Text>
            <Text type={'secondary'} className={'text-xs'}>
              {[
                t.proderRev !== null ? `rev ${t.proderRev}` : null,
                t.process,
                t.area,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Text>
          </List.Item>
        )}
      />
    </div>
  );
};

export default ProderProgressPage;
